import axios from 'axios';
import { getToken, clearAuth } from '../utils/auth';
import { News, Category, User, PaginatedResponse, ApiResponse } from '../types/api';

// Базовый URL для API
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/autofill';

// Создаем экземпляр axios с базовыми настройками
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Добавляем интерцептор для автоматической вставки токена в заголовки
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Обработка ошибок API
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Обработка ошибки 401 (Unauthorized)
    if (error.response?.status === 401) {
      // Очищаем токен и перенаправляем на страницу входа
      clearAuth();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API для аутентификации
export const authApi = {
  login: (username: string, password: string) => 
    api.post('/token/', { username, password }),
  
  register: (userData: {
    username: string;
    email: string;
    password1: string;
    password2: string;
  }) => api.post('/auth/register/', userData),
  
  refreshToken: (refresh: string) => 
    api.post('/token/refresh/', { refresh }),
  
  verifyToken: (token: string) => 
    api.post('/token/verify/', { token }),
  
  updateProfile: (userId: number, profileData: {
    first_name?: string;
    last_name?: string;
    email?: string;
    username?: string;
  }) => api.patch(`/users/${userId}/`, profileData),
};

// Types for news API
export interface NewsApiParams {
  page?: number;
  page_size?: number;
  category?: string;
  search?: string;
  ordering?: string;
}

// API для новостей
export const newsApi = {
  getNews: (params: NewsApiParams = {}) =>
    api.get<{
      count: number;
      pages: number;
      current: number;
      results: News[];
    }>('/news/', { params }),
  
  getNewsById: (id: number) =>
    api.get<News>(`/news/${id}/`),
    
  getEditorPicks: () =>
    api.get<News[]>('/editor-picks/'),
    
  getTopRated: () =>
    api.get<News[]>('/top-rated/'),
    
  getNewsByCategory: (slug: string, params: NewsApiParams = {}) =>
    api.get<PaginatedResponse<News>>(`/category/${slug}/`, { params }),
    
  getRelatedNews: (id: number) =>
    api.get<News[]>(`/news/${id}/related/`),
    
  getUserNews: (userId: number) =>
    api.get<News[]>(`/users/${userId}/news/`),
};

// API для категорий
export const categoriesApi = {
  getCategories: () =>
    api.get<Category[]>('/categories/'),
    
  getCategory: (slug: string) =>
    api.get<Category>(`/category/${slug}/`),
};

// API для рейтингов
export const ratingsApi = {
  rateNews: (newsId: number, value: number) => 
    api.post('/ratings/', { news: newsId, value }),
    
  getUserRating: (newsId: number) => 
    api.get(`/ratings/my-rating/?news=${newsId}`),
};

export default api;